import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError, useApolloClient } from '@apollo/client';

import {
  useErpGroupDiplomasQuery,
  useUploadDiplomaFileMutation,
  useRemoveDiplomaFileMutation,
  usePreviewDiplomaMatchMutation,
  ErpDiplomaDownloadUrlDocument,
  ErpDiplomaDownloadUrlQuery,
  ErpDiplomaDownloadUrlQueryVariables,
  DiplomaLanguageEnum,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { useStableRefetch } from './useStableRefetch';
import {
  IGroupDiplomaRow,
  IDiplomaMatchPreview,
  diplomaErrorMessage,
} from './diplomaModel';

/**
 * Spec 0016 — the group's diploma roster. One row per student, each carrying
 * the PL and EN slots.
 *
 * Mirrors `useGroupRoster`: `cache-and-network` so a revisit shows cached rows
 * while refreshing, `notifyOnNetworkStatusChange` so `loading` reflects the
 * background refresh, `skip` without a `groupId`, and a stable `refetch`
 * identity (see `useStableRefetch` — an unstable one loops with
 * `cache-and-network`).
 */
interface IUseGroupDiplomas {
  rows: IGroupDiplomaRow[];
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

export const useGroupDiplomas = (groupId?: string): IUseGroupDiplomas => {
  const { data, loading, error, refetch } = useErpGroupDiplomasQuery({
    variables: { groupId: groupId || '' },
    skip: !groupId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  return {
    rows: data?.groupDiplomas ?? [],
    loading,
    error,
    refetch: useStableRefetch(refetch),
  };
};

/* ------------------------------------------------------------------ */

export interface IUploadDiplomaArgs {
  studentId: string;
  groupId: string;
  language: DiplomaLanguageEnum;
  file: File;
  /** Only ever `true` for a slot the operator explicitly confirmed replacing. */
  replaceExisting?: boolean;
  /**
   * Opt OUT of the failure toast. Only the bulk runner sets it — it renders a
   * per-row error itself, and 40 rejected files must not raise 40 toasts. The
   * default is deliberately "toast", so no other caller can fail in silence.
   */
  suppressToast?: boolean;
}

export interface IDiplomaActionResult {
  success: boolean;
  /** Localized message for inline (per-row) display; absent on success. */
  errorMessage?: string;
}

interface IDiplomaMutationsHook {
  /** Attach/replace one PDF in one student's PL or EN slot. */
  upload: (args: IUploadDiplomaArgs) => Promise<IDiplomaActionResult>;
  /** Detach a single diploma. */
  remove: (diplomaId: string) => Promise<IDiplomaActionResult>;
  /** Dry-run the bulk attach from file NAMES only — writes nothing. */
  match: (
    groupId: string,
    fileNames: string[],
  ) => Promise<IDiplomaMatchPreview | null>;
  /** Mint a short-lived link and open it. Never prefetched — see below. */
  download: (diplomaId: string) => Promise<void>;
  /** Diploma ids with a download request in flight — drives button `disabled`. */
  downloadingIds: string[];
  pending: boolean;
}

/**
 * Spec 0016 — diploma lifecycle (`uploadStudentDiploma` /
 * `deleteStudentDiploma` / `matchDiplomaFiles` / `diplomaDownloadUrl`).
 *
 * Every action raises a toast on failure, and `upload`/`remove` ALSO return the
 * message inline so a caller that has a natural place for it (a bulk row, a
 * slot) can render it there. The single exception is `upload({ suppressToast })`
 * — the bulk runner opts out because it shows one error per row.
 */
export const useDiplomaMutations = (
  onChanged?: () => void,
): IDiplomaMutationsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const client = useApolloClient();

  const [uploadRun] = useUploadDiplomaFileMutation();
  const [removeRun] = useRemoveDiplomaFileMutation();
  const [matchRun] = usePreviewDiplomaMatchMutation();
  const [pending, setPending] = useState(false);
  /** Synchronous double-click guard; `downloadingIds` is its rendered mirror. */
  const downloadingRef = useRef<Set<string>>(new Set());
  const [downloadingIds, setDownloadingIds] = useState<string[]>([]);

  const toastError = useCallback(
    (title: string, text?: string) => {
      addToast({
        type: GlobalToastTypeNames.NetworkError,
        data: { type: ToastTypes.error, title, text },
      });
    },
    [addToast],
  );

  const upload: IDiplomaMutationsHook['upload'] = useCallback(
    async ({
      studentId,
      groupId,
      language,
      file,
      replaceExisting,
      suppressToast,
    }) => {
      // A rejected upload (backend magic-byte check, the 10 MB cap, a dropped
      // connection) must never end as a stopped spinner and nothing else: the
      // `accept="…pdf"` filter only inspects the OS-reported extension, so the
      // backend refusing the bytes is an ordinary, reachable outcome.
      const fail = (errorMessage: string): IDiplomaActionResult => {
        if (!suppressToast) {
          toastError(t('pages.diplomas.toast.uploadError'), errorMessage);
        }
        return { success: false, errorMessage };
      };

      try {
        const result = await uploadRun({
          variables: {
            studentId,
            groupId,
            language,
            file,
            replaceExisting: Boolean(replaceExisting),
          },
        });
        const payload = result.data?.uploadStudentDiploma;
        if (payload?.success) {
          onChanged?.();
          return { success: true };
        }
        return fail(diplomaErrorMessage(t, payload?.customErrors));
      } catch (e) {
        return fail((e as Error).message);
      }
    },
    [uploadRun, onChanged, toastError, t],
  );

  const remove: IDiplomaMutationsHook['remove'] = useCallback(
    async (diplomaId) => {
      setPending(true);
      try {
        const result = await removeRun({ variables: { diplomaId } });
        const payload = result.data?.deleteStudentDiploma;
        if (payload?.success) {
          onChanged?.();
          return { success: true };
        }
        const errorMessage = diplomaErrorMessage(t, payload?.customErrors);
        toastError(t('pages.diplomas.toast.deleteError'), errorMessage);
        return { success: false, errorMessage };
      } catch (e) {
        const errorMessage = (e as Error).message;
        toastError(t('pages.diplomas.toast.deleteError'), errorMessage);
        return { success: false, errorMessage };
      } finally {
        setPending(false);
      }
    },
    [removeRun, onChanged, toastError, t],
  );

  const match: IDiplomaMutationsHook['match'] = useCallback(
    async (groupId, fileNames) => {
      setPending(true);
      try {
        const result = await matchRun({ variables: { groupId, fileNames } });
        const payload = result.data?.matchDiplomaFiles;
        if (payload?.success && payload.preview) return payload.preview;
        toastError(
          t('pages.diplomas.toast.matchError'),
          diplomaErrorMessage(t, payload?.customErrors),
        );
        return null;
      } catch (e) {
        toastError(t('pages.diplomas.toast.matchError'), (e as Error).message);
        return null;
      } finally {
        setPending(false);
      }
    },
    [matchRun, toastError, t],
  );

  /**
   * The download link is minted per click and expires, so it is deliberately
   * NOT prefetched into the list and never cached (`no-cache`) — a URL fetched
   * while rendering the table would already be stale by the time it is used.
   *
   * The blank tab is opened SYNCHRONOUSLY, before the await: browsers only
   * treat `window.open` as user-initiated inside the click handler's own task,
   * so opening it after the round-trip would be swallowed by the popup blocker.
   * We then point that already-granted tab at the resolved URL. Everything the
   * in-flight guard does above it is synchronous too, so that property holds.
   *
   * The guard itself lives in a ref, not in state: a second click landing in the
   * same tick would still see the old state value and would mint a second link
   * into a second orphan tab.
   */
  const download: IDiplomaMutationsHook['download'] = useCallback(
    async (diplomaId) => {
      if (downloadingRef.current.has(diplomaId)) return;
      downloadingRef.current.add(diplomaId);
      setDownloadingIds(Array.from(downloadingRef.current));

      const tab = window.open('', '_blank');
      try {
        const { data } = await client.query<
          ErpDiplomaDownloadUrlQuery,
          ErpDiplomaDownloadUrlQueryVariables
        >({
          query: ErpDiplomaDownloadUrlDocument,
          variables: { diplomaId },
          fetchPolicy: 'no-cache',
        });

        const url = data?.diplomaDownloadUrl?.url;
        if (!url) {
          tab?.close();
          toastError(t('pages.diplomas.toast.downloadError'));
          return;
        }
        if (tab) {
          tab.location.href = url;
        } else {
          // Popup blocked despite the synchronous open — tell the operator
          // rather than silently doing nothing.
          toastError(t('pages.diplomas.toast.popupBlocked'));
        }
      } catch (e) {
        tab?.close();
        toastError(
          t('pages.diplomas.toast.downloadError'),
          (e as Error).message,
        );
      } finally {
        downloadingRef.current.delete(diplomaId);
        setDownloadingIds(Array.from(downloadingRef.current));
      }
    },
    [client, toastError, t],
  );

  return { upload, remove, match, download, downloadingIds, pending };
};

/* ------------------------------------------------------------------ */

/** One queued file in the bulk-attach run. */
export interface IDiplomaUploadJob {
  /**
   * The join key — see `IPickedDiplomaFile`. NOT the file name: a batch may
   * legitimately contain two files called `diploma.pdf`.
   */
  uid: string;
  /** Display only. */
  fileName: string;
  file: File;
  studentId: string;
  language: DiplomaLanguageEnum;
  replaceExisting: boolean;
}

export interface IDiplomaUploadOutcome {
  /** Matches the originating job's `uid`. */
  uid: string;
  fileName: string;
  success: boolean;
  errorMessage?: string;
}

export interface IDiplomaBulkProgress {
  running: boolean;
  /** Files finished (succeeded or failed) — drives «12 из 40». */
  done: number;
  total: number;
  outcomes: IDiplomaUploadOutcome[];
}

const EMPTY_PROGRESS: IDiplomaBulkProgress = {
  running: false,
  done: 0,
  total: 0,
  outcomes: [],
};

/** Parallel uploads in flight. Keeps the backend from being hit 40-wide. */
const UPLOAD_CONCURRENCY = 3;

/**
 * Spec 0016 — the bulk-attach runner: one `uploadStudentDiploma` mutation per
 * file (the backend has no batch endpoint), at most {@link UPLOAD_CONCURRENCY}
 * in flight, reporting progress by file count and a per-file outcome so failed
 * rows can be retried individually.
 *
 * A failing file never aborts the run — its error is recorded and the pool
 * moves on, so one bad PDF cannot strand the other 39.
 */
export const useDiplomaBulkUpload = (
  upload: (args: IUploadDiplomaArgs) => Promise<IDiplomaActionResult>,
  groupId: string,
): {
  progress: IDiplomaBulkProgress;
  run: (jobs: IDiplomaUploadJob[]) => Promise<IDiplomaUploadOutcome[]>;
  reset: () => void;
} => {
  const [progress, setProgress] = useState<IDiplomaBulkProgress>(
    EMPTY_PROGRESS,
  );

  const reset = useCallback(() => setProgress(EMPTY_PROGRESS), []);

  const run = useCallback(
    async (jobs: IDiplomaUploadJob[]): Promise<IDiplomaUploadOutcome[]> => {
      if (jobs.length === 0) return [];

      setProgress({
        running: true,
        done: 0,
        total: jobs.length,
        outcomes: [],
      });

      const outcomes: IDiplomaUploadOutcome[] = [];
      let cursor = 0;

      // Worker-pool: each worker pulls the next index until the queue drains,
      // so exactly UPLOAD_CONCURRENCY requests are in flight at any moment.
      const worker = async (): Promise<void> => {
        for (;;) {
          const index = cursor;
          cursor += 1;
          if (index >= jobs.length) return;

          const job = jobs[index];
          // eslint-disable-next-line no-await-in-loop
          const result = await upload({
            studentId: job.studentId,
            groupId,
            language: job.language,
            file: job.file,
            replaceExisting: job.replaceExisting,
            // The modal renders a per-row error; toasting here would stack one
            // notification per failed file.
            suppressToast: true,
          });

          const outcome: IDiplomaUploadOutcome = {
            uid: job.uid,
            fileName: job.fileName,
            success: result.success,
            errorMessage: result.errorMessage,
          };
          outcomes.push(outcome);

          setProgress((prev) => ({
            ...prev,
            done: prev.done + 1,
            outcomes: [...prev.outcomes, outcome],
          }));
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(UPLOAD_CONCURRENCY, jobs.length) }, () =>
          worker(),
        ),
      );

      setProgress((prev) => ({ ...prev, running: false }));
      return outcomes;
    },
    [upload, groupId],
  );

  return { progress, run, reset };
};
